/**
 * Finnhub quote response normalization — provider quirks → canonical markets dataShape.
 *
 * SERVER-SIDE ONLY. Validates the raw Finnhub /quote payload (already Zod-parsed)
 * and maps it to the metrics declared in DATA_SHAPE_KEYS['markets.quote']. All
 * conversion and rounding happens here; `glanceView` receives clean, typed values.
 *
 * Immutability: returns a fresh object, never mutates the input.
 *
 * Finnhub API docs: https://finnhub.io/docs/api/quote
 * Quote endpoint: GET https://finnhub.io/api/v1/quote?symbol=AAPL&token=<key>
 *
 * V1 LIMITATION (documented):
 *   The params basket may contain multiple symbols (e.g. ['AAPL','MSFT']),
 *   but v1 fetches and represents ONLY THE FIRST symbol. The basket is preserved
 *   in the cache key so a future v2 upgrade path is clean (the key already
 *   encodes the full basket, so cached multi-symbol responses won't collide
 *   with single-symbol responses for the same first ticker).
 *
 * MARKET CLOSED (Finnhub behavior):
 *   When the market is closed, Finnhub returns `d=0, dp=0, c=prevClose` — i.e.,
 *   current price equals previous close, change is zero. We normalize this
 *   honestly: direction='flat', change=0, changePct=0. We do NOT attempt to
 *   detect "market closed" from the payload (Finnhub provides no explicit
 *   closed flag in /quote). The renderer uses a stale timestamp to convey age.
 */

import { z } from 'zod';

// ---------------------------------------------------------------------------
// Raw Finnhub /quote response schema (validates the upstream payload before use).
// SERVER-SIDE ONLY — the client never sees raw provider payloads (ADR-002 §5).
// ---------------------------------------------------------------------------

/**
 * Zod schema for the Finnhub /quote response.
 * Ref: https://finnhub.io/docs/api/quote
 *
 * Fields:
 *   c  — current price (number, required)
 *   d  — change vs previous close (number or null; Finnhub sends 0 when market is closed)
 *   dp — percent change (number or null; same zero behavior)
 *   h  — high price of the day (optional — not surfaced in dataShape)
 *   l  — low price of the day (optional — not surfaced in dataShape)
 *   o  — open price of the day (optional — not surfaced in dataShape)
 *   pc — previous close price (number, required per docs; nullable here for safety)
 *   t  — timestamp of the last update (unix epoch seconds, optional)
 *
 * We validate only the fields we consume; additional Finnhub fields are permitted
 * (passthrough) so future Finnhub API additions don't break the adapter.
 */
export const rawFinnhubQuoteSchema = z.object({
  c: z.number(), // current price (required)
  d: z.number().nullable().optional(), // change (null possible when market never opened)
  dp: z.number().nullable().optional(), // percent change
  h: z.number().optional(), // day high (not surfaced; retained for passthrough)
  l: z.number().optional(), // day low (not surfaced)
  o: z.number().optional(), // open (not surfaced)
  pc: z.number().nullable().optional(), // previous close
  t: z.number().int().optional(), // timestamp epoch seconds
});

export type RawFinnhubQuote = z.infer<typeof rawFinnhubQuoteSchema>;

// ---------------------------------------------------------------------------
// Normalized markets data shape — the canonical output of normalize().
// Keys MUST match DATA_SHAPE_KEYS['markets.quote']:
//   price, change, changePct, direction, currency, prevClose
// ---------------------------------------------------------------------------

/**
 * Normalized markets metrics. Exported so glanceView and tests can reference
 * the type without importing from the raw shape.
 *
 * `direction` encodes the sign of `change` as a semantic label, so the renderer
 * can apply color/glyph without re-computing the sign. Possible values:
 *   'up'   — change > 0
 *   'down' — change < 0
 *   'flat' — change === 0 (includes market-closed state where d=0)
 *
 * `currency` is always 'USD' in v1 (Finnhub /quote is US-centric; international
 * tickers are out of scope for the free tier data we use).
 *
 * `prevClose` may be null if the Finnhub response omits the `pc` field (edge case:
 * newly listed tickers or Finnhub API errors). The renderer treats null prevClose
 * as "no comparison available".
 */
export interface MarketsData {
  /** Current price, rounded to 2 decimal places. */
  readonly price: number;
  /** Change vs previous close (positive = up, negative = down, 0 = flat/market-closed). */
  readonly change: number;
  /** Percent change vs previous close, rounded to 2 decimal places. */
  readonly changePct: number;
  /** Direction derived from change sign: 'up' | 'down' | 'flat'. */
  readonly direction: 'up' | 'down' | 'flat';
  /** Currency code — always 'USD' in v1 (Finnhub US-centric free tier). */
  readonly currency: string;
  /** Previous close price, rounded to 2 decimal places. Null if unavailable. */
  readonly prevClose: number | null;
}

/** Context injected alongside the raw payload (server-side). */
export interface MarketsNormalizeContext {
  /** The symbol being normalized (first symbol from the basket). */
  readonly symbol: string;
}

/**
 * Map a Finnhub /quote raw payload → canonical MarketsData.
 *
 * Pure: returns a fresh object, never mutates `raw`.
 * Total: every missing optional field has a safe fallback.
 *
 * Rounding: price/prevClose to 2dp (financial convention).
 * Direction: derived from change; 0 is 'flat' (covers market-closed Finnhub zeros).
 */
export function normalize(raw: RawFinnhubQuote): MarketsData {
  const price = Math.round(raw.c * 100) / 100;
  const change = Math.round((raw.d ?? 0) * 100) / 100;
  const changePct = Math.round((raw.dp ?? 0) * 100) / 100;
  const prevClose = raw.pc != null ? Math.round(raw.pc * 100) / 100 : null;

  const direction: 'up' | 'down' | 'flat' =
    change > 0 ? 'up' : change < 0 ? 'down' : 'flat';

  return {
    price,
    change,
    changePct,
    direction,
    currency: 'USD', // v1: Finnhub free tier is US-centric
    prevClose,
  };
}
